import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Alert, Spin, Row, Col, Table, Tag, Space, Empty, Button, Progress, Popconfirm,
  Modal, Radio, InputNumber, Switch, Select, TimePicker, Input, Tooltip as AntTooltip, theme as antdTheme,
} from 'antd';
import {
  EyeOutlined, SendOutlined, ExclamationCircleOutlined, ArrowUpOutlined,
  ArrowLeftOutlined, LinkOutlined, InteractionOutlined, ReloadOutlined, RocketOutlined,
  CloseCircleOutlined, UserDeleteOutlined, PauseCircleOutlined, PlayCircleOutlined, ThunderboltOutlined,
  UnorderedListOutlined, EditOutlined, HistoryOutlined, SearchOutlined, TrophyOutlined, StopOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Matches backend Date.getUTCDay() convention: 0=Sun..6=Sat.
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const sortDays = (days) => [...days].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));

const WEEKDAYS = [
  { label: 'M',   full: 'Monday',    value: 1 },
  { label: 'T',   full: 'Tuesday',   value: 2 },
  { label: 'W',   full: 'Wednesday', value: 3 },
  { label: 'T',   full: 'Thursday',  value: 4 },
  { label: 'F',   full: 'Friday',    value: 5 },
  { label: 'S',   full: 'Saturday',  value: 6 },
  { label: 'Sun', full: 'Sunday',    value: 0 },
];

const TIMEZONES = [
  { label: 'UTC (UTC+0)',                      value: 'UTC' },
  { label: 'London (UTC+0/+1)',                value: 'Europe/London' },
  { label: 'Paris / Berlin (UTC+1/+2)',        value: 'Europe/Paris' },
  { label: 'India (UTC+5:30)',                 value: 'Asia/Kolkata' },
  { label: 'Singapore / Beijing (UTC+8)',      value: 'Asia/Singapore' },
  { label: 'Tokyo / Seoul (UTC+9)',            value: 'Asia/Tokyo' },
  { label: 'Sydney (UTC+10/+11)',              value: 'Australia/Sydney' },
  { label: 'Los Angeles / Seattle (UTC-8/-7)', value: 'America/Los_Angeles' },
  { label: 'Denver / Phoenix (UTC-7/-6)',      value: 'America/Denver' },
  { label: 'Chicago / Dallas (UTC-6/-5)',      value: 'America/Chicago' },
  { label: 'New York / Miami (UTC-5/-4)',      value: 'America/New_York' },
  { label: 'São Paulo (UTC-3)',                value: 'America/Sao_Paulo' },
];

// Convert a chosen time-of-day (dayjs, hour/minute only) in `tz` to a UTC
// "HH:mm" string, anchored to today's date in `tz` for the conversion.
const toUTCTimeString = (dayjsTime, tz) => {
  const anchored = dayjs().hour(dayjsTime.hour()).minute(dayjsTime.minute()).second(0);
  const d = new Date(Date.UTC(anchored.year(), anchored.month(), anchored.date(), anchored.hour(), anchored.minute(), 0));
  const tzParts = (zone) => new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d).reduce((a, p) => { if (p.type !== 'literal') a[p.type] = +p.value; return a; }, {});
  const toMs = (o) => Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute, o.second);
  const offset = toMs(tzParts(tz)) - toMs(tzParts('UTC'));
  const utc = new Date(d.getTime() - offset);
  return `${String(utc.getUTCHours()).padStart(2, '0')}:${String(utc.getUTCMinutes()).padStart(2, '0')}`;
};

// Inverse of toUTCTimeString: given a stored "HH:mm" UTC time, return a dayjs
// value whose LOCAL hour/minute equal the wall-clock time as seen in `tz` —
// so re-opening the TimePicker shows exactly what was originally picked.
const utcTimeToDisplayDayjs = (utcTime, tz) => {
  const [hh, mm] = utcTime.split(':').map(Number);
  const utcMoment = new Date();
  utcMoment.setUTCHours(hh, mm, 0, 0);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(utcMoment).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  const placeholder = new Date();
  placeholder.setHours(parseInt(parts.hour, 10) % 24, parseInt(parts.minute, 10), 0, 0); // browser-local setter
  return dayjs(placeholder);
};

// Format a UTC ISO timestamp as { day, time } strings rendered in `tz` (falls
// back to the viewer's browser timezone if `tz` is not provided), with the
// timezone abbreviation appended to the time (e.g. "9:00 AM IST" or, for
// zones without a common short name, "9:00 AM GMT+5:30").
const formatInTz = (isoOrDate, tz) => {
  if (!isoOrDate) return { day: '—', time: '—', full: '—' };
  const d = new Date(isoOrDate);
  const zone = tz || undefined; // undefined => Intl uses the browser's local zone
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: zone });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: zone });
  return { day, time, full: `${day}, ${time}` };
};

const STATUS_COLORS = {
  sent:    '#94a3b8',
  opened:  '#6366f1',
  clicked: '#9DC533',
  bounced: '#ef4444',
};

const STATUS_TAG = {
  sent:    'default',
  opened:  'processing',
  clicked: 'success',
  bounced: 'error',
};

const MetricCard = ({ icon, label, value, color, bg, suffix = '' }) => {
  const { token: themeToken } = antdTheme.useToken();
  return (
    <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', height: '100%' }}
      styles={{ body: { padding: '16px 18px', height: '100%', display: 'flex', alignItems: 'center', gap: 14 } }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { style: { fontSize: 20, color } })}
      </div>
      <div style={{ minWidth: 0 }}>
        <Text style={{ color: themeToken.colorTextSecondary, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{label}</Text>
        <div style={{ fontSize: 24, fontWeight: 800, color: themeToken.colorText, lineHeight: 1.2, marginTop: 2 }}>
          {value}<span style={{ fontSize: 13, color, fontWeight: 600, marginLeft: 1 }}>{suffix}</span>
        </div>
      </div>
    </Card>
  );
};

const Reports = () => {
  const { token: themeToken } = antdTheme.useToken();
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [emailsPage, setEmailsPage] = useState(1);
  const EMAIL_PAGE_SIZE = 50;
  const [stats, setStats] = useState(null);
  const [clicks, setClicks] = useState({ urlSummary: [], events: [] });
  const [listStats, setListStats] = useState([]);
  const [batchStats, setBatchStats] = useState([]);
  const [live, setLive] = useState(null);
  const [pausing, setPausing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editScheduleModal, setEditScheduleModal] = useState(false);
  const [editDaily, setEditDaily] = useState(200);
  const [editDayMode, setEditDayMode] = useState('every');
  const [editDaysSelected, setEditDaysSelected] = useState([1, 2, 3, 4, 5]);
  const [editFixedTime, setEditFixedTime] = useState(false);
  const [editClock, setEditClock] = useState(null);       // dayjs — used in "every day" mode
  const [editDayTimes, setEditDayTimes] = useState({});   // { [dayValue]: dayjs } — used in "specific days" mode
  const [editTz, setEditTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailStatusFilter, setEmailStatusFilter] = useState(null);
  const [emailBatchFilter, setEmailBatchFilter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  const fetchEmails = async (p = 1) => {
    setEmailsLoading(true);
    try {
      const params = { page: p, limit: EMAIL_PAGE_SIZE };
      if (emailSearch.trim()) params.search = emailSearch.trim();
      if (emailStatusFilter) params.status = emailStatusFilter;
      if (emailBatchFilter) params.batch = emailBatchFilter;
      const e = await axios.get(`/api/campaigns/${id}/emails`, { params });
      setEmails(e.data.data);
      setEmailsTotal(e.data.total);
    } catch {
      // non-fatal: emails table just stays empty
    } finally {
      setEmailsLoading(false);
    }
  };

  const fetchAll = async (showSpinner = false) => {
    if (showSpinner) { setLoading(true); setError(''); }
    try {
      const [c, s, cl, lv, ls, bs] = await Promise.all([
        axios.get(`/api/campaigns/${id}`),
        axios.get(`/api/campaigns/${id}/stats`),
        axios.get(`/api/campaigns/${id}/clicks`),
        axios.get(`/api/campaigns/${id}/live`).catch(() => null),
        axios.get(`/api/campaigns/${id}/list-stats`).catch(() => null),
        axios.get(`/api/campaigns/${id}/batch-stats`).catch(() => null),
      ]);
      setCampaign(c.data);
      setStats(s.data);
      setClicks(cl.data);
      if (lv?.data) setLive(lv.data);
      if (ls?.data) setListStats(ls.data.lists || []);
      if (bs?.data) setBatchStats(bs.data.batches || []);
      await fetchEmails(1);
      return c.data;
    } catch {
      if (showSpinner) setError('Failed to load campaign report');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(true);
  }, [id]);

  // Auto-refresh every 5 s while campaign is sending
  useEffect(() => {
    if (!campaign || campaign.status !== 'sending') return;
    const interval = setInterval(() => fetchAll(false), 5000);
    return () => clearInterval(interval);
  }, [campaign?.status]);

  useEffect(() => { if (!loading) fetchEmails(emailsPage); }, [emailsPage]);
  // Debounced server-side search + immediate filter changes — reset to page 1
  // and re-query, so results span every page, not just the one loaded. Guard
  // against double-fetching: only fetch directly when already on page 1;
  // otherwise setEmailsPage(1) alone triggers the page-change effect above.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      if (emailsPage === 1) fetchEmails(1); else setEmailsPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [emailSearch]);
  useEffect(() => {
    if (loading) return;
    if (emailsPage === 1) fetchEmails(1); else setEmailsPage(1);
  }, [emailStatusFilter, emailBatchFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll(false);
    setRefreshing(false);
  };

  const handleResendUnopened = async () => {
    setResending(true);
    try {
      const res = await axios.post(`/api/campaigns/${id}/resend-unopened`);
      await fetchAll(false);
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const handlePause = async () => {
    setPausing(true);
    try {
      await axios.post(`/api/campaigns/${id}/pause`);
      await fetchAll(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to pause');
    } finally { setPausing(false); }
  };

  // Kill switch — cancels all remaining drip batches and marks the campaign
  // complete right away. Already-sent emails are untouched; nothing more goes out.
  const handleCancelDrip = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`/api/campaigns/${id}/cancel-drip`);
      await fetchAll(false);
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel drip');
    } finally { setCancelling(false); }
  };

  const handleResume = async () => {
    setPausing(true);
    try {
      const res = await axios.post(`/api/campaigns/${id}/resume`);
      await fetchAll(false);
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to resume');
    } finally { setPausing(false); }
  };

  // Toggle a weekday in/out of editDaysSelected, keeping editDayTimes in
  // sync — a newly-checked day gets a sensible default time, an unchecked
  // day drops its entry.
  const toggleEditDay = (dayValue) => {
    setEditDaysSelected(prev => {
      const isActive = prev.includes(dayValue);
      if (isActive) {
        setEditDayTimes(pt => { const c = { ...pt }; delete c[dayValue]; return c; });
        return prev.filter(v => v !== dayValue);
      }
      setEditDayTimes(pt => {
        if (pt[dayValue]) return pt;
        const lastDay = prev[prev.length - 1];
        return { ...pt, [dayValue]: pt[lastDay] || dayjs().hour(9).minute(0) };
      });
      return [...prev, dayValue];
    });
  };

  const openEditSchedule = () => {
    setEditDaily(live?.daily_limit || 200);
    const days = Array.isArray(live?.send_days) && live.send_days.length ? live.send_days : null;
    setEditDayMode(days ? 'specific' : 'every');
    setEditDaysSelected(days || [1, 2, 3, 4, 5]);
    // Prefer the timezone the schedule was actually set in; fall back to the
    // viewer's own browser timezone only if none was stored.
    const tz = live?.daily_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    setEditTz(tz);
    if (live?.day_times && Object.keys(live.day_times).length) {
      setEditFixedTime(true);
      const dt = {};
      for (const [k, v] of Object.entries(live.day_times)) dt[Number(k)] = utcTimeToDisplayDayjs(v, tz);
      setEditDayTimes(dt);
      setEditClock(live?.daily_time ? utcTimeToDisplayDayjs(live.daily_time, tz) : null);
    } else if (live?.daily_time) {
      setEditFixedTime(true);
      setEditClock(utcTimeToDisplayDayjs(live.daily_time, tz));
      setEditDayTimes({});
    } else {
      setEditFixedTime(false);
      setEditClock(null);
      setEditDayTimes({});
    }
    setEditScheduleModal(true);
  };

  const handleSaveSchedule = async () => {
    if (editDayMode === 'specific' && !editDaysSelected.length) { alert('Select at least one day'); return; }
    const body = {
      dailyLimit: editDaily,
      sendDays: editDayMode === 'specific' ? editDaysSelected : [],
    };
    if (editFixedTime && editDayMode === 'specific') {
      const times = {};
      for (const d of editDaysSelected) {
        const t = editDayTimes[d];
        if (!t) { alert(`Pick a time for ${DAY_ABBR[d]}`); return; }
        times[d] = toUTCTimeString(dayjs(t), editTz);
      }
      body.dayTimesUTC = times;
      body.dailyTimeUTC = null;
      body.dailyTimezone = editTz;
    } else if (editFixedTime) {
      if (!editClock) { alert('Pick a daily time'); return; }
      body.dailyTimeUTC = toUTCTimeString(dayjs(editClock), editTz);
      body.dailyTimezone = editTz;
      body.dayTimesUTC = null;
    } else {
      body.dailyTimeUTC = null;
      body.dailyTimezone = null;
      body.dayTimesUTC = null;
    }
    setSavingSchedule(true);
    try {
      const res = await axios.put(`/api/campaigns/${id}/drip-schedule`, body);
      await fetchAll(false);
      alert(res.data.message);
      setEditScheduleModal(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update schedule');
    } finally { setSavingSchedule(false); }
  };

  const pieData = stats ? [
    { name: 'Sent',    value: stats.sent },
    { name: 'Opened',  value: stats.opened },
    { name: 'Clicked', value: stats.clicked },
    { name: 'Bounced', value: stats.bounced },
  ].filter(d => d.value > 0) : [];

  const columns = [
    {
      title: 'Subscriber', key: 'sub',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.Subscriber?.name || (r.recipient_email ? 'Manual' : '—')}</div>
          <div style={{ color: themeToken.colorTextSecondary, fontSize: 12 }}>{r.Subscriber?.email || r.recipient_email || `ID: ${r.subscriber_id}`}</div>
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: s => <Tag color={STATUS_TAG[s]} style={{ fontWeight: 500 }}>{s.charAt(0).toUpperCase() + s.slice(1)}</Tag>,
    },
    {
      title: 'Batch', dataIndex: 'batch_number', width: 80, align: 'center',
      render: b => b ? <Tag color="cyan" style={{ margin: 0 }}>#{b}</Tag> : <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>—</Text>,
    },
    {
      title: 'Sent At', dataIndex: 'sent_at', width: 160,
      render: d => d ? <Text style={{ fontSize: 12 }}>{new Date(d).toLocaleString()}</Text> : '—',
    },
    {
      title: 'Opened', dataIndex: 'opened_at', width: 160,
      render: d => d ? <Text style={{ fontSize: 12, color: '#6366f1' }}>{new Date(d).toLocaleString()}</Text> : '—',
    },
    {
      title: 'Clicked', dataIndex: 'clicked_at', width: 160,
      render: d => d ? <Text style={{ fontSize: 12, color: '#9DC533' }}>{new Date(d).toLocaleString()}</Text> : '—',
    },
    {
      title: 'Bounced', dataIndex: 'bounced_at', width: 160,
      render: d => d ? <Text style={{ fontSize: 12, color: '#ef4444' }}>{new Date(d).toLocaleString()}</Text> : '—',
    },
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/campaigns')}
          style={{ borderRadius: 10, marginTop: 2 }}
        >
          Campaigns
        </Button>
        <div style={{ flex: 1 }}>
          <Title level={3} style={{ margin: 0 }}>
            {campaign ? campaign.name : 'Campaign Report'}
          </Title>
          <Text style={{ color: themeToken.colorTextSecondary }}>
            {campaign ? `Subject: ${campaign.subject}` : 'Loading…'}
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
          <Button
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
            style={{ borderRadius: 10 }}
          >
            Refresh
          </Button>
          {campaign && campaign.status === 'sending' && live && !live.is_paused && (
            <Popconfirm
              title="Pause this campaign?"
              description="Sending stops immediately. You can resume later."
              onConfirm={handlePause}
              okText="Pause" okButtonProps={{ danger: true }}
            >
              <Button icon={<PauseCircleOutlined />} danger loading={pausing} style={{ borderRadius: 10 }}>
                Pause
              </Button>
            </Popconfirm>
          )}
          {live && live.is_paused && (
            <Button icon={<PlayCircleOutlined />} type="primary" onClick={handleResume} loading={pausing} style={{ borderRadius: 10 }}>
              Resume
            </Button>
          )}
          {live && live.daily_limit > 0 && (campaign?.status === 'sending' || live.is_paused) && (
            <Button icon={<EditOutlined />} onClick={openEditSchedule} style={{ borderRadius: 10 }}>
              Edit Schedule
            </Button>
          )}
          {live && live.daily_limit > 0 && (campaign?.status === 'sending' || live.is_paused) && (
            <Popconfirm
              title="Cancel the remaining drip?"
              description={`Stops all future batches now and marks the campaign complete. ${live.pending > 0 ? `${live.pending.toLocaleString()} recipient(s) will not be emailed.` : ''} This can't be undone.`}
              onConfirm={handleCancelDrip}
              okText="Cancel Drip" okButtonProps={{ danger: true }}
              cancelText="Keep Sending"
            >
              <Button icon={<StopOutlined />} danger loading={cancelling} style={{ borderRadius: 10 }}>
                Cancel Drip
              </Button>
            </Popconfirm>
          )}
          {campaign && campaign.status === 'completed' && stats && stats.sent > 0 && (
            <Button
              icon={<RocketOutlined />}
              onClick={handleResendUnopened}
              loading={resending}
              type="primary"
              style={{ borderRadius: 10 }}
            >
              Resend to Non-openers ({stats.sent})
            </Button>
          )}
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />}

      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>
      ) : stats ? (
        <>
          {/* ── Kill-switch / paused banner ── */}
          {live && live.is_paused && (
            <Alert
              type="error"
              showIcon
              icon={<ThunderboltOutlined />}
              message="Campaign paused"
              description={
                <span>
                  {live.paused_reason || 'This campaign is paused.'} Sending has stopped.
                  {live.pending > 0 && ` ${live.pending.toLocaleString()} recipient(s) not yet sent.`} Click Resume to continue.
                </span>
              }
              action={<Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={handleResume} loading={pausing}>Resume</Button>}
              style={{ marginBottom: 20, borderRadius: 12 }}
            />
          )}

          {/* ── Live send monitor (while sending) ── */}
          {live && (campaign?.status === 'sending' || live.is_paused) && (
            <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: live.is_paused ? '#f59e0b' : '#16a34a', display: 'inline-block', boxShadow: live.is_paused ? 'none' : '0 0 0 3px rgba(22,163,74,0.2)' }} />
                <Text strong>{live.is_paused ? 'Sending paused' : 'Sending live'}</Text>
                <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>· auto-refreshing</Text>
                {live.daily_limit > 0 && (
                  <Tag color="green" style={{ marginLeft: 4 }}>
                    {live.daily_limit.toLocaleString()}/day
                    {Array.isArray(live.send_days) && live.send_days.length > 0 && ` · ${sortDays(live.send_days).map(d => DAY_ABBR[d]).join(', ')} only`}
                    {live.next_batch_at && !live.is_paused && ` · next batch ${formatInTz(live.next_batch_at, live.daily_timezone).full}`}
                  </Tag>
                )}
              </div>
              <Progress
                percent={live.progress}
                status={live.is_paused ? 'exception' : 'active'}
                strokeColor={live.is_paused ? '#f59e0b' : '#9DC533'}
              />
              <Row gutter={[16, 12]} style={{ marginTop: 14 }}>
                {[
                  { label: 'Sent',       value: live.sent,     color: '#16a34a' },
                  { label: 'Bounced',    value: live.bounced,  color: '#dc2626' },
                  { label: 'Remaining',  value: live.pending,  color: '#6366f1' },
                  { label: 'Target',     value: live.total,    color: '#6b7280' },
                  { label: 'Dupes Prevented', value: live.duplicates_prevented, color: '#8b5cf6' },
                ].map(m => (
                  <Col xs={12} sm={8} md={4} key={m.label}>
                    <Text style={{ fontSize: 11, color: themeToken.colorTextSecondary, display: 'block' }}>{m.label}</Text>
                    <Text style={{ fontSize: 20, fontWeight: 700, color: m.color }}>{(m.value ?? 0).toLocaleString()}</Text>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* Metric Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }} align="stretch">
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<SendOutlined />} label="Delivered" value={stats.total} color="#6366f1" bg="#ede9fe" />
            </Col>
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<EyeOutlined />} label="Open Rate" value={stats.openRate} suffix="%" color="#9DC533" bg="#EEF6D0" />
            </Col>
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<ArrowUpOutlined />} label="Click Rate" value={stats.clickRate} suffix="%" color="#3b82f6" bg="#dbeafe" />
            </Col>
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<InteractionOutlined />} label="Total Clicks" value={stats.totalClicks ?? 0} color="#8b5cf6" bg="#ede9fe" />
            </Col>
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<ExclamationCircleOutlined />} label="Bounce Rate" value={stats.bounceRate} suffix="%" color="#ef4444" bg="#fef2f2" />
            </Col>
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<CloseCircleOutlined />} label="Complaint Rate" value={stats.complaintRate ?? '0'} suffix="%" color="#b91c1c" bg="#fee2e2" />
            </Col>
            <Col xs={12} sm={8} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
              <MetricCard icon={<UserDeleteOutlined />} label="Unsub Rate" value={stats.unsubscribeRate ?? '0'} suffix="%" color="#6b7280" bg="#f3f4f6" />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            {pieData.length > 0 && (
              <Col xs={24} lg={10}>
                <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>Status Breakdown</Text>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                        {pieData.map(entry => (
                          <Cell key={entry.name} fill={STATUS_COLORS[entry.name.toLowerCase()] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ borderRadius: 10, border: '1px solid #f1f5f9' }} />
                      <Legend iconType="circle" iconSize={10} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            )}

            <Col xs={24} lg={pieData.length > 0 ? 14 : 24}>
              <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', height: '100%' }}>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>Delivery Summary</Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { label: 'Total Emails',       count: stats.total,                    color: themeToken.colorText, pct: '100%' },
                    { label: 'Sent (pending open)', count: stats.sent,                   color: '#94a3b8', pct: stats.total > 0 ? ((stats.sent / stats.total) * 100).toFixed(1) + '%' : '—' },
                    { label: 'Opened (incl. clicked)', count: stats.opened + stats.clicked, color: '#6366f1', pct: stats.openRate + '%' },
                    { label: 'Unique Clickers',     count: stats.clicked,                color: '#9DC533', pct: stats.clickRate + '%' },
                    { label: 'Bounced',             count: stats.bounced,                color: '#ef4444', pct: stats.bounceRate + '%' },
                    { label: 'Complaints',          count: stats.complained ?? 0,        color: '#b91c1c', pct: (stats.complaintRate ?? '0') + '%' },
                    { label: 'Unsubscribed',        count: stats.unsubscribed ?? 0,      color: '#6b7280', pct: (stats.unsubscribeRate ?? '0') + '%' },
                  ].map(({ label, count, color, pct }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: themeToken.colorBgLayout, borderRadius: 10 }}>
                      <Space size={8}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                        <Text style={{ color: themeToken.colorText, fontSize: 13 }}>{label}</Text>
                      </Space>
                      <Space size={16}>
                        <Text strong style={{ color }}>{count}</Text>
                        <Text style={{ color: themeToken.colorTextTertiary, fontSize: 12, minWidth: 40, textAlign: 'right' }}>{pct}</Text>
                      </Space>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Click URL Breakdown */}
          {clicks.urlSummary.length > 0 && (
            <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20 }}>
              <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>
                <LinkOutlined style={{ color: '#8b5cf6', marginRight: 8 }} />
                Link Click Breakdown
              </Text>
              <Table
                dataSource={clicks.urlSummary}
                rowKey="url"
                size="small"
                pagination={false}
                scroll={{ x: 500 }}
                columns={[
                  {
                    title: 'URL', dataIndex: 'url', ellipsis: true,
                    render: url => (
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#6366f1', fontSize: 12 }}
                        onClick={e => e.stopPropagation()}>
                        {url}
                      </a>
                    ),
                  },
                  {
                    title: 'Total Clicks', dataIndex: 'totalClicks', width: 120, align: 'center',
                    render: v => (
                      <span style={{ fontWeight: 700, color: '#8b5cf6', fontSize: 14 }}>{v}</span>
                    ),
                  },
                  {
                    title: 'Unique Clickers', dataIndex: 'uniqueSubscribers', width: 140, align: 'center',
                    render: v => <Tag color="purple">{v} subscriber{v !== 1 ? 's' : ''}</Tag>,
                  },
                ]}
              />
            </Card>
          )}

          {/* Performance by List */}
          {listStats.length > 0 && (
            <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20 }}>
              <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>
                <UnorderedListOutlined style={{ color: '#6366f1', marginRight: 8 }} />
                Performance by List
              </Text>
              <Table
                dataSource={listStats}
                rowKey={r => r.id ?? 'other'}
                size="small"
                pagination={false}
                scroll={{ x: 700 }}
                columns={[
                  {
                    title: 'List', dataIndex: 'name',
                    render: (name, r) => (
                      <div>
                        <Text strong style={{ fontSize: 13 }}>{name}</Text>
                        {r.listSize != null && (
                          <Text style={{ display: 'block', fontSize: 11, color: themeToken.colorTextTertiary }}>
                            {r.listSize} member{r.listSize !== 1 ? 's' : ''} total
                          </Text>
                        )}
                      </div>
                    ),
                  },
                  { title: 'Sent', dataIndex: 'total', width: 80, align: 'center', render: v => <Text strong>{v}</Text> },
                  {
                    title: 'Open Rate', dataIndex: 'openRate', width: 100, align: 'center',
                    render: v => <Tag color="success" style={{ margin: 0 }}>{v}%</Tag>,
                  },
                  {
                    title: 'Click Rate', dataIndex: 'clickRate', width: 100, align: 'center',
                    render: v => <Tag color="blue" style={{ margin: 0 }}>{v}%</Tag>,
                  },
                  {
                    title: 'Bounce Rate', dataIndex: 'bounceRate', width: 105, align: 'center',
                    render: v => <Tag color="error" style={{ margin: 0 }}>{v}%</Tag>,
                  },
                  {
                    title: 'Complaint Rate', dataIndex: 'complaintRate', width: 115, align: 'center', responsive: ['lg'],
                    render: v => <Tag color="volcano" style={{ margin: 0 }}>{v}%</Tag>,
                  },
                  {
                    title: 'Unsub Rate', dataIndex: 'unsubscribeRate', width: 105, align: 'center', responsive: ['lg'],
                    render: v => <Tag color="default" style={{ margin: 0 }}>{v}%</Tag>,
                  },
                ]}
              />
            </Card>
          )}

          {/* Performance by Batch — only meaningful for a multi-batch drip send.
              Shows the day/time each batch went out alongside its rates, and
              flags the best-performing batch, to spot which send window works. */}
          {batchStats.length > 1 && (() => {
            const withSignal = batchStats.filter(b => b.sentAt && b.total > 0);
            const bestBatch = withSignal.length > 1
              ? withSignal.reduce((best, b) => parseFloat(b.openRate) > parseFloat(best.openRate) ? b : best, withSignal[0])
              : null;
            return (
              <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20 }}>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>
                  <HistoryOutlined style={{ color: '#6366f1', marginRight: 8 }} />
                  Performance by Batch
                </Text>
                <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary, display: 'block', marginBottom: 16 }}>
                  Compare each send's day and time to see what worked best. Click a rate column to sort.
                </Text>
                <Table
                  dataSource={batchStats}
                  rowKey="batch"
                  size="small"
                  pagination={false}
                  scroll={{ x: 820 }}
                  rowClassName={r => (bestBatch && r.batch === bestBatch.batch ? 'best-batch-row' : '')}
                  columns={[
                    {
                      title: 'Batch', dataIndex: 'batch', width: 100,
                      render: (b, r) => (
                        <Space size={4}>
                          <Tag color="cyan" style={{ margin: 0, fontWeight: 600 }}>#{b}</Tag>
                          {bestBatch && r.batch === bestBatch.batch && (
                            <AntTooltip title="Highest open rate among these batches">
                              <TrophyOutlined style={{ color: '#f59e0b' }} />
                            </AntTooltip>
                          )}
                        </Space>
                      ),
                    },
                    {
                      title: 'Day', key: 'day', width: 110,
                      sorter: (a, b) => new Date(a.sentAt || 0) - new Date(b.sentAt || 0),
                      render: (_, r) => (
                        <Text style={{ fontSize: 12, fontWeight: 500, color: r.sentAt ? undefined : themeToken.colorTextTertiary }}>
                          {formatInTz(r.sentAt, live?.daily_timezone).day}
                        </Text>
                      ),
                    },
                    {
                      title: `Time${live?.daily_timezone ? ` (${live.daily_timezone.split('/').pop().replace('_', ' ')})` : ''}`,
                      key: 'time', width: 130,
                      render: (_, r) => (
                        <Text style={{ fontSize: 12, color: r.sentAt ? themeToken.colorTextSecondary : themeToken.colorTextTertiary }}>
                          {formatInTz(r.sentAt, live?.daily_timezone).time}
                        </Text>
                      ),
                    },
                    { title: 'Sent', dataIndex: 'total', width: 70, align: 'center', render: v => <Text strong>{v}</Text> },
                    {
                      title: 'Open Rate', dataIndex: 'openRate', width: 105, align: 'center',
                      sorter: (a, b) => parseFloat(a.openRate) - parseFloat(b.openRate),
                      render: v => <Tag color="success" style={{ margin: 0 }}>{v}%</Tag>,
                    },
                    {
                      title: 'Click Rate', dataIndex: 'clickRate', width: 105, align: 'center',
                      sorter: (a, b) => parseFloat(a.clickRate) - parseFloat(b.clickRate),
                      render: v => <Tag color="blue" style={{ margin: 0 }}>{v}%</Tag>,
                    },
                    {
                      title: 'Bounce Rate', dataIndex: 'bounceRate', width: 110, align: 'center',
                      sorter: (a, b) => parseFloat(a.bounceRate) - parseFloat(b.bounceRate),
                      render: v => <Tag color="error" style={{ margin: 0 }}>{v}%</Tag>,
                    },
                    { title: 'Complaint Rate', dataIndex: 'complaintRate', width: 115, align: 'center', responsive: ['lg'], render: v => <Tag color="volcano" style={{ margin: 0 }}>{v}%</Tag> },
                    { title: 'Unsub Rate', dataIndex: 'unsubscribeRate', width: 105, align: 'center', responsive: ['lg'], render: v => <Tag color="default" style={{ margin: 0 }}>{v}%</Tag> },
                  ]}
                />
                <style>{`.best-batch-row td { background: ${themeToken.colorSuccessBg} !important; }`}</style>
              </Card>
            );
          })()}

          {/* Individual Records */}
          <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>Individual Records</Text>
            <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <Input
                placeholder="Search by name or email…"
                prefix={<SearchOutlined style={{ color: themeToken.colorTextTertiary }} />}
                value={emailSearch}
                onChange={e => setEmailSearch(e.target.value)}
                allowClear
                style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 320, borderRadius: 10 }}
              />
              <Select
                value={emailStatusFilter ?? 'all'}
                onChange={v => setEmailStatusFilter(v === 'all' ? null : v)}
                style={{ minWidth: 150 }}
                options={[
                  { label: 'All Statuses', value: 'all' },
                  { label: 'Sent',    value: 'sent' },
                  { label: 'Opened',  value: 'opened' },
                  { label: 'Clicked', value: 'clicked' },
                  { label: 'Bounced', value: 'bounced' },
                  { label: 'Pending', value: 'pending' },
                ]}
              />
              {batchStats.length > 1 && (
                <Select
                  value={emailBatchFilter ?? 'all'}
                  onChange={v => setEmailBatchFilter(v === 'all' ? null : v)}
                  style={{ minWidth: 140 }}
                  options={[
                    { label: 'All Batches', value: 'all' },
                    ...batchStats.map(b => ({ label: `Batch #${b.batch}`, value: b.batch })),
                  ]}
                />
              )}
            </div>
            <Table
              dataSource={emails}
              columns={columns}
              rowKey="id"
              size="small"
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No records yet" /> }}
              loading={emailsLoading}
              pagination={{
                current: emailsPage, pageSize: EMAIL_PAGE_SIZE, total: emailsTotal,
                onChange: (p) => setEmailsPage(p),
                showTotal: t => `${t} record${t !== 1 ? 's' : ''}`,
                showSizeChanger: false,
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      ) : (
        <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, textAlign: 'center', padding: '40px 0' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">No data available for this campaign yet</Text>} />
        </Card>
      )}

      {/* ── Edit Schedule Modal — change daily limit / days / fixed time while a drip is running ── */}
      <Modal
        title={<Space><EditOutlined style={{ color: '#6366f1' }} /><span>Edit Drip Schedule</span></Space>}
        open={editScheduleModal}
        onCancel={() => setEditScheduleModal(false)}
        onOk={handleSaveSchedule}
        okText="Save Changes"
        okButtonProps={{ loading: savingSchedule, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={480}
        destroyOnHidden
      >
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Alert
            type="info"
            showIcon
            message="Changes apply to future batches only"
            description="Today's batch (if it already went out) is never re-sent — only the upcoming schedule changes."
            style={{ borderRadius: 10 }}
          />

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Emails per day</Text>
            <InputNumber min={1} max={100000} step={50} value={editDaily} onChange={v => setEditDaily(v || 0)} style={{ width: '100%', borderRadius: 8 }} size="large" />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Send on</Text>
            <Radio.Group value={editDayMode} onChange={e => setEditDayMode(e.target.value)} style={{ width: '100%', marginBottom: editDayMode === 'specific' ? 10 : 0 }}>
              <Radio.Button value="every" style={{ width: '50%', textAlign: 'center' }}>All days</Radio.Button>
              <Radio.Button value="specific" style={{ width: '50%', textAlign: 'center' }}>Specific days</Radio.Button>
            </Radio.Group>
            {editDayMode === 'specific' && (
              <Space size={6} wrap>
                {WEEKDAYS.map(d => {
                  const active = editDaysSelected.includes(d.value);
                  return (
                    <Button
                      key={d.value}
                      size="small"
                      title={d.full}
                      onClick={() => toggleEditDay(d.value)}
                      style={{
                        borderRadius: 8, minWidth: 38, fontWeight: 600,
                        background: active ? '#9DC533' : undefined,
                        borderColor: active ? '#9DC533' : undefined,
                        color: active ? '#fff' : undefined,
                      }}
                    >
                      {d.label}
                    </Button>
                  );
                })}
              </Space>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editFixedTime ? 10 : 0 }}>
              <Text strong>Fixed time each day</Text>
              <Switch checked={editFixedTime} onChange={setEditFixedTime} />
            </div>
            {editFixedTime ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Select showSearch style={{ width: '100%' }} value={editTz} onChange={setEditTz} optionFilterProp="label" options={TIMEZONES} />
                {editDayMode === 'specific' ? (
                  editDaysSelected.length ? (
                    sortDays(editDaysSelected).map(d => (
                      <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text style={{ width: 42, fontSize: 13, fontWeight: 500 }}>{DAY_ABBR[d]}</Text>
                        <TimePicker
                          use12Hours format="hh:mm A" style={{ flex: 1 }}
                          value={editDayTimes[d] || null}
                          onChange={val => setEditDayTimes(pt => ({ ...pt, [d]: val }))}
                          placeholder="Time"
                        />
                      </div>
                    ))
                  ) : (
                    <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                      Select at least one day above to set its time.
                    </Text>
                  )
                ) : (
                  <TimePicker
                    use12Hours format="hh:mm A" style={{ width: '100%' }}
                    value={editClock}
                    onChange={setEditClock}
                    placeholder="Time"
                  />
                )}
              </div>
            ) : (
              <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                Off — each batch fires ~24h after the previous one, whenever that happened to be.
              </Text>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Reports;
